import { useMemo } from 'react';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { usePartnerNoteStore } from '../../lib/stores/partnerNoteStore';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { saveLeadNoteWithAttachments } from '../leads/leadDrawerWrites';
import type { EntityAdapter } from '../shared/activity-drawer/types';

/**
 * Task 5 "Partner drawer unificato": adapter entità "partner" per il drawer condiviso
 * Attività/Nota (`ActivityNoteDrawer`), consumato da `PartnerStatusUpdateDrawer.tsx`.
 *
 * Gemello di `useLeadDrawerAdapter` (leadDrawerAdapter.ts) ma senza slot di stato: un
 * partner non ha fase/assegnatario/commerciale/motivo perso da committare qui — la fase
 * pipeline partner è gestita altrove (PartnerStageSlot, Task 4), quindi
 * `validateEntityChanges`/`commitEntityChanges` sono no-op (a parte la guardia sull'id).
 *
 * Stessa forma dell'adapter già costruito inline in `usePartnerComposerSaves.ts` (Task 1)
 * per il composer inline — `saveNote` passa dalla STESSA `saveLeadNoteWithAttachments` con
 * `linkage: { partner_id }`, `createNote` è lo stesso adattamento posizionale verso
 * `usePartnerNoteStore.createNote`, `afterActivitySaved` resta no-op (nessun workflow di
 * automazione per i partner). Estratto in un hook a parte (non riusato da
 * `usePartnerComposerSaves`) per non toccare quel file, già testato E2E in Task 1.
 */
export function usePartnerDrawerAdapter(params: { partnerId: string; pipelineContext?: string }): {
  adapter: EntityAdapter;
} {
  const { partnerId, pipelineContext } = params;
  const { toast } = useApp();
  const { user } = useAuth();
  const { uploadNoteAttachment } = useLeadNoteStore();
  const { createNote: createPartnerNote } = usePartnerNoteStore();

  const adapter: EntityAdapter = useMemo(
    () => ({
      kind: 'partner',
      entity: { id: partnerId },
      // Un partner non è un cliente: nessun clientId, quindi niente fetch di dati
      // cliente/attendee nella shell condivisa (guardie già presenti in useDrawerData).
      clientId: undefined,
      entityUrl: () => `${window.location.origin}/partners/${partnerId}`,
      linkageFields: () => ({ partner_id: partnerId }),
      defaultAssigneeId: null,
      validateEntityChanges: () => (partnerId ? null : 'Partner non disponibile: riprova'),
      commitEntityChanges: async () => {},
      saveNote: async (html: string, files: File[]) =>
        saveLeadNoteWithAttachments({
          leadId: partnerId,
          html,
          files,
          pipelineContext,
          createNote: async (entityId: string, content: string) => {
            const organizationId = await getCurrentUserOrganizationId();
            if (!organizationId || !user?.id) return null;
            return createPartnerNote(entityId, content, organizationId, user.id);
          },
          uploadNoteAttachment,
          toast,
          linkage: { partner_id: partnerId },
        }),
      // Nessun trigger di workflow per i partner (a differenza del lead).
      afterActivitySaved: async () => {},
    }),
    [partnerId, pipelineContext, createPartnerNote, uploadNoteAttachment, toast, user?.id]
  );

  return { adapter };
}
